import { FALLBACK_OPEN_TIME } from '@/utils/openingHours';

/**
 * Vehicle drop-off defaults that follow the booking (#4124).
 *
 * Values are `yyyy-MM-dd'T'HH:mm` civil strings, the same shape the
 * scheduling step uses for arrival and pickup.
 */
interface DropoffInput {
  schedulingType: 'exact' | 'floating';
  arrivalAtStr: string;
}

/**
 * Drop-off field value while the user has not edited it.
 *
 * Exact: the appointment itself — the car is there when the work starts.
 * Floating: the arrival is a midnight day marker, so the car is expected at
 * the day's opening time (`openTime`, 08:00 when the workshop hours are
 * unknown), or at a later clock the arrival carries (a calendar slot) —
 * never at midnight. Empty when there is no booking time yet.
 */
export function suggestedDropoffAt(input: DropoffInput & { openTime?: string | null }): string {
  if (!input.arrivalAtStr) return '';
  if (input.schedulingType === 'exact') return input.arrivalAtStr;
  const [day = '', clock = ''] = input.arrivalAtStr.split('T');
  if (!day) return '';
  const openTime = input.openTime || FALLBACK_OPEN_TIME;
  // Both are zero-padded HH:mm, so string order is time order.
  return `${day}T${clock > openTime ? clock : openTime}`;
}

/**
 * Latest drop-off the booking allows, or null without a booking time.
 *
 * Exact: the appointment start — a car that arrives later misses its slot.
 * Floating: the end of the booked day. During-the-day work is scheduled
 * after the drop-off, so a car brought in at noon is still that day's work.
 */
export function latestDropoffAt(input: DropoffInput): string | null {
  if (!input.arrivalAtStr) return null;
  if (input.schedulingType === 'exact') return input.arrivalAtStr;
  const [day = ''] = input.arrivalAtStr.split('T');
  return day ? `${day}T23:59` : null;
}

/** Whether a drop-off fits the booking: same or earlier than `latestDropoffAt`. */
export function dropoffFitsBooking(input: DropoffInput & { dropoffAtStr: string }): boolean {
  const latest = latestDropoffAt(input);
  if (!input.dropoffAtStr || latest == null) return true;
  return input.dropoffAtStr <= latest;
}
